import React, { useState, useEffect, useCallback } from "react";
import { useTemplate, useFields } from "../../context/MetadataContext";
import FormSection from "./FormSection";
import LinkedRecordsSection from "./LinkedRecordsSection";
import LoadingSpinner from "../ui/LoadingSpinner";
import ErrorDisplay from "../ui/ErrorDisplay";
import { ComponentErrorBoundary } from "../ErrorBoundary";
import { LinkIcon, PlusIcon } from "@heroicons/react/24/outline";

/**
 * Dynamic Form Renderer Component
 *
 * Renders forms dynamically based on metadata templates and field definitions.
 * Supports sections, validation, conditional logic, auto-save functionality,
 * and linked record displays for relationships between entities.
 */
export default function DynamicFormRenderer({
  templateId,
  fieldsId = "project-fields", // Default to project-fields for backward compatibility
  data = {},
  onDataChange,
  onSave,
  onValidationChange,
  autoSave = true,
  autoSaveInterval = 30000, // 30 seconds
  showProgress = true,
  className = "",
  disabled = false,
  onLinkedRecordNavigate, // Callback for navigating to linked records
  onLinkedRecordEdit, // Callback for editing linked records
  onLinkedRecordAdd, // Callback for adding new linked records
  onLinkedRecordUnlink, // Callback for unlinking records
  ...props
}) {
  // Use proper hooks for metadata loading
  const {
    template,
    loading: templateLoading,
    error: templateError,
  } = useTemplate(templateId);
  const {
    fields: fieldDefinitions,
    loading: fieldsLoading,
    error: fieldsError,
  } = useFields(fieldsId);

  // Component state
  const [fields, setFields] = useState({});
  const [formData, setFormData] = useState(data);
  const [validationErrors, setValidationErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle"); // 'idle', 'saving', 'saved', 'error'

  // Calculate loading and error states
  const isLoading = templateLoading || fieldsLoading;
  const error = templateError || fieldsError;

  // Process field definitions when they load
  useEffect(() => {
    if (fieldDefinitions && !fieldsLoading && !fieldsError) {
      // Create field lookup map
      const fieldMap = {};
      fieldDefinitions.forEach((field) => {
        fieldMap[field.id] = field;
      });
      setFields(fieldMap);
    }
  }, [fieldDefinitions, fieldsLoading, fieldsError]);

  // Update form data when external data changes
  useEffect(() => {
    setFormData(data);
  }, [data]);

  // Auto-save functionality
  useEffect(() => {
    if (!autoSave || !isDirty || !onSave) return;

    const autoSaveTimer = setTimeout(async () => {
      try {
        setAutoSaveStatus("saving");
        await onSave(formData);
        setAutoSaveStatus("saved");
        setIsDirty(false);

        // Reset status after 2 seconds
        setTimeout(() => setAutoSaveStatus("idle"), 2000);
      } catch (err) {
        console.error("Auto-save failed:", err);
        setAutoSaveStatus("error");
      }
    }, autoSaveInterval);

    return () => clearTimeout(autoSaveTimer);
  }, [autoSave, isDirty, formData, onSave, autoSaveInterval]);

  // Handle field value changes
  const handleFieldChange = useCallback(
    (fieldId, value) => {
      const newFormData = {
        ...formData,
        [fieldId]: value,
      };

      setFormData(newFormData);
      setIsDirty(true);
      setAutoSaveStatus("idle");

      // Trigger external change handler
      if (onDataChange) {
        onDataChange(newFormData);
      }

      // Clear validation error for this field if it exists
      if (validationErrors[fieldId]) {
        const newErrors = { ...validationErrors };
        delete newErrors[fieldId];
        setValidationErrors(newErrors);

        if (onValidationChange) {
          onValidationChange(newErrors);
        }
      }
    },
    [formData, validationErrors, onDataChange, onValidationChange]
  );

  // Handle field validation
  const handleFieldValidation = useCallback(
    (fieldId, errors) => {
      const newValidationErrors = {
        ...validationErrors,
        [fieldId]: errors.length > 0 ? errors : undefined,
      };

      // Remove undefined entries
      Object.keys(newValidationErrors).forEach((key) => {
        if (newValidationErrors[key] === undefined) {
          delete newValidationErrors[key];
        }
      });

      setValidationErrors(newValidationErrors);

      if (onValidationChange) {
        onValidationChange(newValidationErrors);
      }
    },
    [validationErrors, onValidationChange]
  );

  // Manual save function
  const handleSave = async () => {
    if (!onSave) return;

    try {
      setAutoSaveStatus("saving");
      await onSave(formData);
      setAutoSaveStatus("saved");
      setIsDirty(false);

      setTimeout(() => setAutoSaveStatus("idle"), 2000);
    } catch (err) {
      console.error("Save failed:", err);
      setAutoSaveStatus("error");
    }
  };

  // Validate all fields
  const validateForm = () => {
    const errors = {};

    if (!template || !template.layout?.sections) return errors;

    template.layout.sections.forEach((section) => {
      // Skip validation for linked-records sections
      if (section.type === "linked-records") return;

      section.fields.forEach((fieldId) => {
        const field = fields[fieldId];
        const value = formData[fieldId];

        if (field && field.validation) {
          const fieldErrors = validateField(field, value);
          if (fieldErrors.length > 0) {
            errors[fieldId] = fieldErrors;
          }
        }
      });
    });

    return errors;
  };

  // Check if form is valid
  const isFormValid = Object.keys(validationErrors).length === 0;

  // Render a section based on its type
  const renderSection = (section, index) => {
    // Handle linked records sections
    if (section.type === "linked-records") {
      const linkedData = formData[section.linkedField];

      return (
        <div key={section.id || index} className="form-section linked-records">
          {section.title && (
            <div className="mb-3">
              <h3 className="text-lg font-medium text-gray-900">
                {section.title}
              </h3>
              {section.description && (
                <p className="text-sm text-gray-600 mt-1">
                  {section.description}
                </p>
              )}
            </div>
          )}

          {/* Handle array of linked records (e.g., multiple valuations) */}
          {Array.isArray(linkedData) && linkedData.length > 0 ? (
            <div className="space-y-2">
              {linkedData.map((linkItem, idx) => {
                const recordId = linkItem[`${section.recordType}Id`];
                return (
                  <LinkedRecordsSection
                    key={recordId || idx}
                    recordType={section.recordType}
                    linkedRecordId={recordId}
                    onNavigate={onLinkedRecordNavigate}
                    onEdit={onLinkedRecordEdit}
                    onAdd={onLinkedRecordAdd}
                    onUnlink={onLinkedRecordUnlink}
                  />
                );
              })}
            </div>
          ) : /* Handle single linked record (e.g., deal to project) */
          linkedData && typeof linkedData === "string" ? (
            <LinkedRecordsSection
              recordType={section.recordType}
              linkedRecordId={linkedData}
              onNavigate={onLinkedRecordNavigate}
              onEdit={onLinkedRecordEdit}
              onAdd={onLinkedRecordAdd}
              onUnlink={onLinkedRecordUnlink}
            />
          ) : (
            /* Handle empty state */
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <LinkIcon className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-600">
                    No {section.recordType} linked
                  </span>
                </div>
                {onLinkedRecordAdd && (
                  <button
                    onClick={() => onLinkedRecordAdd(section.recordType)}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add {section.recordType}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      );
    }

    // Handle regular form sections
    return (
      <FormSection
        key={section.id || index}
        section={section}
        fields={fields}
        formData={formData}
        validationErrors={validationErrors}
        disabled={disabled}
        onFieldChange={handleFieldChange}
        onFieldValidation={handleFieldValidation}
      />
    );
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner size="lg" message="Loading form configuration..." />
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="p-4">
        <ErrorDisplay
          error={error}
          title="Form Configuration Error"
          message="Failed to load form template or field definitions."
        />
      </div>
    );
  }

  // Ensure template is loaded before rendering
  if (!template) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner size="lg" message="Loading template..." />
      </div>
    );
  }

  // Render form
  return (
    <ComponentErrorBoundary componentName="DynamicFormRenderer">
      <div className={`dynamic-form-renderer ${className}`} {...props}>
        {/* Form Header */}
        {template.name && (
          <div className="form-header mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">
              {template.name}
            </h2>
            {template.description && (
              <p className="text-gray-600 mt-1">{template.description}</p>
            )}
          </div>
        )}

        {/* Progress & Status Indicators */}
        {showProgress && (
          <div className="form-status mb-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {autoSave && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Auto-save:</span>
                  <AutoSaveStatus status={autoSaveStatus} />
                </div>
              )}

              {isDirty && (
                <span className="text-sm text-amber-600">Unsaved changes</span>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <ValidationStatus
                errors={validationErrors}
                isValid={isFormValid}
              />

              {!autoSave && onSave && (
                <button
                  onClick={handleSave}
                  disabled={disabled || autoSaveStatus === "saving"}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {autoSaveStatus === "saving" ? "Saving..." : "Save"}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Form Sections */}
        {template.layout?.sections ? (
          <div
            className={`form-content grid gap-0 ${
              template.layout?.columns === 2 ? "lg:grid-cols-2" : "grid-cols-1"
            }`}
          >
            {template.layout.sections.map(renderSection)}
          </div>
        ) : (
          <div className="p-4 text-center text-gray-500">
            No form sections defined in template.
          </div>
        )}
      </div>
    </ComponentErrorBoundary>
  );
}

/**
 * Auto-save Status Indicator
 */
function AutoSaveStatus({ status }) {
  const statusConfig = {
    idle: { text: "Ready", color: "text-gray-500" },
    saving: { text: "Saving...", color: "text-blue-600" },
    saved: { text: "Saved", color: "text-green-600" },
    error: { text: "Save Failed", color: "text-red-600" },
  };

  const config = statusConfig[status] || statusConfig.idle;

  return <span className={`text-sm ${config.color}`}>{config.text}</span>;
}

/**
 * Validation Status Indicator
 */
function ValidationStatus({ errors, isValid }) {
  const errorCount = Object.keys(errors).length;

  if (errorCount === 0) {
    return <span className="text-sm text-green-600">✓ Valid</span>;
  }

  return (
    <span className="text-sm text-red-600">
      {errorCount} error{errorCount !== 1 ? "s" : ""}
    </span>
  );
}

/**
 * Field validation helper function
 */
function validateField(field, value) {
  const errors = [];

  if (!field.validation) return errors;

  field.validation.forEach((rule) => {
    switch (rule.type) {
      case "required":
        if (!value || (typeof value === "string" && value.trim() === "")) {
          errors.push(rule.message || `${field.label} is required`);
        }
        break;

      case "min_length":
        if (value && value.length < rule.value) {
          errors.push(
            rule.message ||
              `${field.label} must be at least ${rule.value} characters`
          );
        }
        break;

      case "max_length":
        if (value && value.length > rule.value) {
          errors.push(
            rule.message ||
              `${field.label} cannot exceed ${rule.value} characters`
          );
        }
        break;

      case "pattern":
        if (value && !new RegExp(rule.value).test(value)) {
          errors.push(rule.message || `${field.label} format is invalid`);
        }
        break;

      default:
        break;
    }
  });

  return errors;
}
